import { DataTable } from '@/components/ui/data-table';
import { useTableUrlState } from '@/hooks/useTableUrlState';
import { cn } from '@/lib/utils';
import { Customer, useCustomerList } from '@/modules/Customers/Customers.api';
import { usePermissions, UserPermissions } from '@/modules/Permissions';
import { ColumnDef } from '@tanstack/react-table';
import { Pencil } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { SecondaryButton } from '../buttons';

export const CustomersTable = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const canEditCustomer = hasPermission(UserPermissions.EDIT_CUSTOMER);

  const columns: ColumnDef<Customer, unknown>[] = [
    {
      id: 'client_name',
      header: 'Customer Name',
      accessorKey: 'client_name',
      enableSorting: false, // Sorting on calculated fields not currently supported
      cell: ({ row }) => {
        return (
          <div className={cn('flex items-center gap-4')}>
            {row.original.client_logo_url && (
              <img
                src={
                  typeof row.original.client_logo_url === 'object'
                    ? row.original.client_logo_url.thumbnails.medium
                    : row.original.client_logo_url
                }
                alt="logo"
                className={cn('size-10 object-contain rounded')}
              />
            )}
            {row.original.client_name}
          </div>
        );
      },
    },
    {
      id: 'client_id',
      header: 'Customer ID',
      accessorKey: 'client_id',
    },
    {
      header: 'Contact Name',
      accessorFn: (customer) => customer.owner?.name || '-',
      enableSorting: false, // Sorting on calculated fields not currently supported
    },
    {
      header: 'Contact Phone',
      accessorFn: (customer) => customer.owner?.phone_number || '-',
      enableSorting: false, // Sorting on calculated fields not currently supported
      cell: ({ row }) => {
        const phone = row.original.owner?.phone_number || '-';
        return (
          <span title={phone} className="block max-w-[140px] truncate">
            {phone}
          </span>
        );
      },
    },
    {
      header: 'Contact Email',
      accessorFn: (customer) => customer.owner?.email || '-',
      enableSorting: false, // Sorting on calculated fields not currently supported
      cell: ({ row }) => {
        const email = row.original.owner?.email || '-';
        return (
          <span title={email} className="block max-w-[140px] truncate">
            {email}
          </span>
        );
      },
    },
    ...(canEditCustomer
      ? [
          {
            id: 'edit_customer',
            header: 'Edit Customer',
            cell: ({ row }: { row: { original: Customer } }) => {
              const navigateToEditCustomer = () => navigate(`/customers/edit/${row.original.client_id}`);
              return (
                <div className="flex justify-center">
                  <SecondaryButton
                    type="button"
                    variant="fitted"
                    size="small"
                    className="bg-white border border-neutral-200 text-neutral-900 
                            rounded-lg px-3 py-1 text-sm font-medium
                            hover:bg-neutral-100 transition"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigateToEditCustomer();
                    }}
                  >
                    <Pencil />
                  </SecondaryButton>
                </div>
              );
            },
            meta: { className: 'w-1/12' },
          } as ColumnDef<Customer, unknown>,
        ]
      : []),
  ];

  const { pagination, setPagination, sorting, setSorting, columnFilters, setColumnFilters } = useTableUrlState();

  const { data, status } = useCustomerList(pagination, sorting, columnFilters);

  if (status === 'error') {
    toast.error('Failed to fetch customers');
    return;
  }

  const handleRowClick = (customer: Customer) => {
    navigate(`/customers/${customer.client_id}/projects/`);
  };

  return (
    <div className={cn('p-5 flex-col gap-3')}>
      <DataTable
        columns={columns}
        data={data?.results}
        totalCount={data?.count}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
        showSearch={true}
        searchPlaceholder="Search Customer Name or ID..."
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        isLoading={status === 'pending'}
        tableRowClickable={() => true}
        onTableRowClick={handleRowClick}
      />
    </div>
  );
};
